import { AutofocusDirective } from '../../../shared/directive/autofocus.directive';
import { FormBaseComponent } from '@anihis/shared/src/shared/base-components/form-base.component';
import { Component, inject, OnInit, ViewChild, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
  MatDialogActions,
  MatDialogModule,
} from '@angular/material/dialog';
import { NgxFileDropEntry, NgxFileDropModule } from 'ngx-file-drop';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslocoModule } from '@jsverse/transloco';
import {
  catchError,
  filter,
  finalize,
  forkJoin,
  Observable,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { HttpEventType } from '@angular/common/http';
import { HttpClient } from '@angular/common/http';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { NameInitialsComponent } from '../name-initials/name-initials.component';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { LimitToPipe } from '@anihis/shared/src/pipes/limit-character.pipe';
import { UploadDocumentDialogStore } from './upload-document-dialog.store';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { environment } from 'apps/anihis-portal/src/environments/environment';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { LoadingStatus } from '../../enums/loading';

@Component({
  selector: 'anihis-upload-document-dialog',
  standalone: true,
  imports: [
    AutofocusDirective,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    TranslocoModule,
    MatButtonModule,
    MatDividerModule,
    NgxFileDropModule,
    MatProgressBarModule,
    MatStepperModule,
    MatDialogActions,
    MatCardModule,
    MatIconModule,
    MatProgressSpinnerModule,
    NameInitialsComponent,
    LimitToPipe,
    MatDialogModule,
  ],
  templateUrl: './upload-document-dialog.component.html',
  styleUrls: ['./upload-document-dialog.component.scss'],
  providers: [UploadDocumentDialogStore],
})
export class UploadDocumentDialogComponent
  extends FormBaseComponent
  implements OnInit
{
  @ViewChild('stepper') stepper!: MatStepper;

  store = inject(UploadDocumentDialogStore);

  dialogRef = inject(MatDialogRef<UploadDocumentDialogComponent>);
  data = inject(MAT_DIALOG_DATA);
  dialogService = inject(MatDialog);

  isSaving = signal(false);
  readonly status = LoadingStatus;
  // Mutable current status used for runtime updates (templates still use `status` enum)
  currentStatus: LoadingStatus = LoadingStatus.Loaded;

  httpClient = inject(HttpClient);
  fileObservables: any;
  formData: any;
  files: any[] = [];
  imageUrl: string | null = null;
  showFile!: boolean;
  isLinear = true;
  isOkVet = false;
  showPin = false;
  submitted = false;
  errorMessage?: string;

  override form = this.fb.group({
    veterinarianUid: ['', Validators.required],
  });

  vetPinForm = this.fb.group({
    vetPin: [undefined, Validators.required],
  });

  constructor() {
    super();

    this.dialogRef.backdropClick().subscribe(() => {
      if (!this.isSaving()) this.confirmClose();
    });
    this.dialogRef.keydownEvents().subscribe((event) => {
      if (event.key === 'Escape' && !this.isSaving()) this.confirmClose();
    });
  }

  confirmClose(): void {
    const ref = this.dialogService.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Confirm Close',
        text: 'All unsaved changes will be lost.',
      },
      disableClose: true,
    });
    ref.afterClosed().subscribe((res) => {
      if (res?.confirmed) this.dialogRef.close();
    });
  }

  extractTextAfterDot(filename: string): string {
    const dotIndex = filename.lastIndexOf('.');
    if (dotIndex === -1 || dotIndex === filename.length - 1) {
      return '';
    }
    const textAfterDot = filename.substring(dotIndex + 1);
    return textAfterDot;
  }

  toggleShowPin() {
    this.showPin = !this.showPin;
  }

  setVeterinarian(uidVeterinarian: string) {
    this.stepper.next();
    this.form.patchValue({ veterinarianUid: uidVeterinarian });
  }

  dropped(files: NgxFileDropEntry[]) {
    if (!this.formData) {
      this.formData = new FormData(); // Ensure formData is initialized
    }

    if (!this.fileObservables) {
      this.fileObservables = [];
    }

    if (files) {
      this.currentStatus = LoadingStatus.Loaded;
    }

    files.forEach((droppedFile) => {
      if (droppedFile.fileEntry?.isFile) {
        const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;

        fileEntry.file((file: File) => {
          const reader = new FileReader();

          reader.onload = (e: any) => {
            const existingFile = this.files.find((f) => f.name === file.name);

            if (!existingFile) {
              const fileObj = {
                name: file.name,
                size: (file.size / 1024).toFixed(2) + ' KB',
                preview: file.type.startsWith('image/')
                  ? e.target.result
                  : undefined,
                originalFile: file,
                documentName: '',
              };

              this.files.push(fileObj);
              this.cd.markForCheck();
            }
          };

          reader.readAsDataURL(file);

          this.fileObservables.push(
            new Observable((observer) => {
              this.formData.append('Files', file, file.name);
              observer.next(undefined);
              observer.complete();
            }),
          );
        });
      }
    });
  }

  deleteFile(file: any) {
    const index = this.files.indexOf(file);
    if (index > -1) {
      this.files.splice(index, 1);
    }

    this.formData = new FormData();
    this.files.forEach((f) => {
      if (f.name !== file.name) {
        this.formData.append('Files', f.originalFile, f.originalFile.name);
        this.formData.append('FileNames', f.documentName ?? '');
      }
    });
  }

  submitPin() {
    const pinValue = this.vetPinForm.controls.vetPin.value;
    const veterinarianUid = this.form.controls.veterinarianUid.value;

    if (!pinValue) {
      this.vetPinForm.controls.vetPin.markAsTouched();
      this.vetPinForm.controls.vetPin.setErrors({ required: true });
      return; // Ne dozvoljavamo slanje praznog PIN-a
    }
    if (!veterinarianUid) {
      this.vetPinForm.controls.vetPin.markAsTouched();
      this.errorMessage = 'Please select a veterinarian first.';
      this.vetPinForm.controls.vetPin.setErrors({ vetNotSelected: true });
      return;
    }

    // clear previous control errors
    this.vetPinForm.controls.vetPin.setErrors(null);
    this.errorMessage = undefined;

    this.store.validateVetPin(veterinarianUid, pinValue).subscribe({
      next: (isValid) => {
        if (isValid) {
          this.isOkVet = true;
          this.stepper.next();
        } else {
          this.isOkVet = false;
          this.errorMessage =
            'Veterinarian PIN is incorrect. Please try again.';
          this.vetPinForm.controls.vetPin.setErrors({ incorrect: true });
          this.vetPinForm.controls.vetPin.markAsTouched();
        }
      },
      error: (err) => {
        console.error('Error validating vet PIN:', err);
        this.errorMessage =
          'An error occurred while verifying the PIN. Try again later.';
        this.vetPinForm.controls.vetPin.setErrors({ serverError: true });
        this.vetPinForm.controls.vetPin.markAsTouched();
      },
    });
  }

  saveChanges() {
    this.submitted = true;

    if (this.files.length === 0) {
      return;
    }

    if (this.checkFormValidity()) return;
    this.isSaving.set(true);
    this.dialogRef.disableClose = true;

    forkJoin(this.fileObservables)
      .pipe(
        tap(() => {
          this.currentStatus = LoadingStatus.Loading;
        }),
        switchMap(() => {
          // Rebuild formData to ensure FileNames are in correct order
          this.formData = new FormData();
          this.files.forEach((f) => {
            this.formData.append('Files', f.originalFile, f.originalFile.name);
            this.formData.append('FileNames', f.documentName ?? '');
          });

          this.formData.append(
            'veterinarianUid',
            this.form.controls.veterinarianUid.value,
          );

          this.formData.append('animalUid', this.data.animalUid);

          return this.httpClient
            .post(`${environment.apiUrl}/Animals/Files`, this.formData, {
              reportProgress: true,
              observe: 'events',
            })
            .pipe(filter((event) => event.type === HttpEventType.Response));
        }),
        catchError((error) => {
          console.error(error);
          this.currentStatus = LoadingStatus.Error;
          return throwError(() => error);
        }),
        finalize(() => {
          this.isSaving.set(false);
          this.dialogRef.disableClose = false;
        }),
      )
      .subscribe({
        next: () => {
          this.currentStatus = LoadingStatus.Loaded;
          this.dialogRef.close({ success: true });
        },
        error: (error) => {
          console.error('Error during upload:', error);
        },
      });
  }

  closeDialog(): void {
    this.dialogRef.close();
  }
}
